import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Upload, Input, message, Breadcrumb, Card, Typography, Space, Tag, Form, InputNumber } from "antd";
import { UploadOutlined, FolderOutlined, InboxOutlined, ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import { baseurl } from "../../../components/lib/settings";

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Dragger } = Upload;

const AdminDelegacoes = () => {
  const [delegations, setDelegations] = useState([]);
  const [selectedDelegation, setSelectedDelegation] = useState(null);
  const [editions, setEditions] = useState([]);
  const [selectedEdition, setSelectedEdition] = useState(null);
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileBase64, setFileBase64] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [description, setDescription] = useState("");

  const [isOnboardModalVisible, setIsOnboardModalVisible] = useState(false);
  const [onboardForm] = Form.useForm();
  const [onboarding, setOnboarding] = useState(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDelegations();
  }, []);

  const fetchDelegations = async () => {
    setLoading(true);
    try {
      const response = await baseurl.get("v3/get_delegation_list/");
      setDelegations(response.data);
    } catch (error) {
      message.error("Erro ao carregar delegações.");
    } finally {
      setLoading(false);
    }
  };

  const fetchEditions = async (delegationId) => {
    setLoading(true);
    try {
      const response = await baseurl.get(`v3/get_editions_by_delegation/${delegationId}/`);
      setEditions(response.data);
    } catch (error) {
      message.error("Erro ao carregar edições.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelegationClick = (record) => {
    setSelectedDelegation(record);
    fetchEditions(record.id);
  };

  const handleEditionClick = (record) => {
    setSelectedEdition(record);
    setIsModalVisible(true);
  };

  const handleFileUpload = (info) => {
    const file = info.file;
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      message.error("O ficheiro deve ter no máximo 5MB!");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setFileBase64(reader.result);
      setFileType(file.type || "application/octet-stream");
    };
    reader.onerror = () => {
      message.error("Erro ao ler ficheiro.");
    };
    reader.readAsDataURL(file);
  };

  const handleUploadSubmit = async () => {
    if (!fileBase64) {
      message.warning("Por favor, carregue um ficheiro.");
      return;
    }
    if (!description) {
      message.warning("Por favor, adicione uma descrição.");
      return;
    }

    setUploading(true);
    try {
      await baseurl.post(`v3/edicoes/${selectedEdition.id}/documents/`, {
        description: description,
        file_base64: fileBase64,
        file_type: fileType,
      });
      message.success("Documento carregado com sucesso!");
      setIsModalVisible(false);
      setFileBase64(null);
      setDescription("");
      setFileType(null);
    } catch (error) {
      message.error("Erro ao carregar o documento.");
    } finally {
      setUploading(false);
    }
  };

  const handleOnboardSubmit = async (values) => {
    setOnboarding(true);
    try {
      await baseurl.post('v3/ngoma/administrator/onboard_delegation/', values);
      message.success("Delegação e conta criadas com sucesso!");
      setIsOnboardModalVisible(false);
      onboardForm.resetFields();
      fetchDelegations();
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        message.error(error.response.data.error);
      } else {
        message.error("Erro ao criar delegação.");
      }
    } finally {
      setOnboarding(false);
    }
  };

  const columnsDelegations = [
    {
      title: "#",
      dataIndex: "id",
      key: "index",
      width: 80,
      render: (_, __, index)=> index+1,
    },
    {
      title: "Descrição da Delegação",
      dataIndex: "description",
      key: "description",
      render: (text) => <Text strong style={{ color: "#374151" }}>{text}</Text>,
    },
    {
      title: "Ações",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Button type="primary" shape="round" icon={<FolderOutlined />} onClick={() => handleDelegationClick(record)}>
          Ver Edições
        </Button>
      ),
    },
  ];

  const columnsEditions = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Descrição da Edição",
      dataIndex: "description",
      key: "description",
      render: (text) => <Text strong style={{ color: "#374151" }}>{text}</Text>,
    },
    {
      title: "Estado",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive) => (
        <Tag color={isActive ? "success" : "default"} style={{ borderRadius: "12px", padding: "0 12px" }}>
          {isActive ? "ACTIVA" : "INACTIVA"}
        </Tag>
      ),
    },
    {
      title: "Ações",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Button type="dashed" shape="round" icon={<UploadOutlined />} onClick={() => handleEditionClick(record)}>
          Carregar Documento
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px", background: "#f3f4f6", minHeight: "100vh" }}>
      <div style={{ marginBottom: "32px" }}>
        <Space direction="vertical" size={4}>
          <Title level={2} style={{ margin: 0, color: "#111827", fontWeight: 700 }}>
            Painel de Delegações
          </Title>
          <Text type="secondary" style={{ fontSize: "15px" }}>
            Selecione uma delegação e carregue documentos para as edições ativas.
          </Text>
        </Space>
      </div>

      <Breadcrumb style={{ marginBottom: "24px", fontSize: "14px", padding: "12px 24px", background: "#fff", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        <Breadcrumb.Item onClick={() => { setSelectedDelegation(null); setSelectedEdition(null); }} style={{ cursor: 'pointer', color: selectedDelegation ? '#1890ff' : '#374151', fontWeight: 500 }}>
          <FolderOutlined style={{ marginRight: 6 }} /> Delegações
        </Breadcrumb.Item>
        {selectedDelegation && (
          <Breadcrumb.Item style={{ color: "#111827", fontWeight: 600 }}>
            {selectedDelegation.description}
          </Breadcrumb.Item>
        )}
      </Breadcrumb>

      <Card 
        bordered={false} 
        style={{ borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}
        title={
          selectedDelegation ? (
            <Space size="middle">
              <Button type="text" shape="circle" icon={<ArrowLeftOutlined />} onClick={() => { setSelectedDelegation(null); setSelectedEdition(null); }} />
              <span style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937" }}>Edições: {selectedDelegation.description}</span>
            </Space>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937" }}>Lista de Delegações</span>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsOnboardModalVisible(true)}>
                Nova Delegação
              </Button>
            </div>
          )
        }
      >
        {!selectedDelegation ? (
          <Table
            dataSource={delegations}
            columns={columnsDelegations}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 50, position: ["bottomCenter"] }}
            rowClassName={() => "hoverable-row"}
          />
        ) : (
          <Table
            dataSource={editions}
            columns={columnsEditions}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10, position: ["bottomCenter"] }}
            rowClassName={() => "hoverable-row"}
          />
        )}
      </Card>

      <Modal
        title={
          <Space>
            <UploadOutlined style={{ color: "#1890ff" }} />
            <span style={{ fontWeight: 600 }}>Carregar Documento - {selectedEdition?.description}</span>
          </Space>
        }
        open={isModalVisible}
        onCancel={() => { setIsModalVisible(false); setFileBase64(null); setDescription(""); }}
        onOk={handleUploadSubmit}
        confirmLoading={uploading}
        okText="Fazer Upload"
        cancelText="Cancelar"
        centered
        width={600}
        okButtonProps={{ shape: "round", size: "large" }}
        cancelButtonProps={{ shape: "round", size: "large" }}
      >
        <div style={{ marginTop: "24px", marginBottom: "24px" }}>
          <Text strong style={{ display: "block", marginBottom: "8px" }}>Ficheiro do Documento</Text>
          <Dragger
            beforeUpload={() => false}
            onChange={handleFileUpload}
            maxCount={1}
            accept=".pdf,.doc,.docx,image/*"
            style={{ padding: "20px", background: "#fafafa", borderColor: "#d9d9d9" }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: "#1890ff" }} />
            </p>
            <p className="ant-upload-text" style={{ fontWeight: 500 }}>Clique ou arraste o ficheiro para esta área</p>
            <p className="ant-upload-hint" style={{ color: "#6b7280" }}>
              Suporta apenas PDF, Imagens e Word. Limite estrito de 5MB.
            </p>
          </Dragger>
        </div>
        <div>
          <Text strong style={{ display: "block", marginBottom: "8px" }}>Descrição Detalhada</Text>
          <TextArea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ex: Regulamento da edição 2026..."
            style={{ borderRadius: "8px", padding: "12px" }}
          />
        </div>
      </Modal>

      <Modal
        title="Nova Delegação Completa"
        open={isOnboardModalVisible}
        onCancel={() => { setIsOnboardModalVisible(false); onboardForm.resetFields(); }}
        onOk={() => onboardForm.submit()}
        confirmLoading={onboarding}
        okText="Criar e Registar"
        cancelText="Cancelar"
        width={700}
      >
        <Form form={onboardForm} layout="vertical" onFinish={handleOnboardSubmit}>
            <Title level={5}>1. Dados do Administrador</Title>
            <Space style={{ display: 'flex' }} align="start">
                <Form.Item name="full_name" label="Nome Completo" rules={[{ required: true }]} style={{ width: '310px' }}>
                    <Input />
                </Form.Item>
                <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]} style={{ width: '310px' }}>
                    <Input />
                </Form.Item>
            </Space>
            <Space style={{ display: 'flex' }} align="start">
                <Form.Item name="user_name" label="Nome de Utilizador" rules={[{ required: true }]} style={{ width: '310px' }}>
                    <Input />
                </Form.Item>
                <Form.Item name="password" label="Palavra-passe" rules={[{ required: true }]} style={{ width: '310px' }}>
                    <Input.Password />
                </Form.Item>
            </Space>

            <Title level={5} style={{ marginTop: '16px' }}>2. Dados da Delegação / Empresa</Title>
            <Space style={{ display: 'flex' }} align="start">
                <Form.Item name="company_name" label="Nome da Empresa" rules={[{ required: true }]} style={{ width: '310px' }}>
                    <Input placeholder="Ex: Rádio Moçambique - Maputo" />
                </Form.Item>
                <Form.Item name="delegation_name" label="Nome da Delegação" rules={[{ required: true }]} style={{ width: '310px' }}>
                    <Input placeholder="Ex: Delegação de Maputo" />
                </Form.Item>
            </Space>
            <Space style={{ display: 'flex' }} align="start">
                <Form.Item name="voting_pricing" label="Preço por Voto (MZN)" rules={[{ required: true }]} style={{ width: '310px' }}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
                <Form.Item name="reference_identifier" label="Identificador de Ref" rules={[{ required: true }]} style={{ width: '310px' }}>
                    <Input placeholder="Ex: 001" maxLength={10} />
                </Form.Item>
            </Space>

            <Title level={5} style={{ marginTop: '16px' }}>3. Primeira Edição</Title>
            <Form.Item name="edition_name" label="Nome da Edição" rules={[{ required: true }]}>
                <Input placeholder="Ex: Edição 2026" />
            </Form.Item>
        </Form>
      </Modal>
      
      {/* Global Style for hoverable rows to add micro-interactions */}
      <style>{`
        .hoverable-row { transition: all 0.2s ease; cursor: pointer; }
        .hoverable-row:hover { background-color: #f8fafc !important; transform: scale(1.002); }
      `}</style>
    </div>
  );
};

export default AdminDelegacoes;
